import clsx from 'clsx';
import { memo } from 'react';

import type { SharedActionsMenuProps } from '../../types/SharedActionsMenuProps';
import type { SharedButtonProps } from '../../types/SharedButtonProps';

import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Layout } from '../Layout/Layout';
import { Menu } from '../Menu/Menu';
import './SplitButton.css';

export type SplitButtonMenuProps = {
  actions: SharedActionsMenuProps['actions'];
  label: string;
  groupedActions?: SharedActionsMenuProps['groupedActions'];
};

export type SplitButtonProps = Omit<
  SharedButtonProps,
  'onToggle' | 'selected' | 'status'
> & {
  /** Related actions opened from the trailing menu trigger. */
  menu: SplitButtonMenuProps;
};

export const SplitButton = memo(function SplitButton({
  menu,
  ...primaryProps
}: SplitButtonProps) {
  const { disabled, isLoading, size, variant = 'secondary' } = primaryProps;
  const isInactive = Boolean(disabled || isLoading);

  return (
    <Layout
      align="stretch"
      className={clsx(
        'eds-split-button',
        `eds-split-button--${variant}`,
        isInactive && 'eds-split-button--inactive',
      )}
      role="group"
      spacing="none"
    >
      <span className="eds-split-button--half eds-split-button--half--start">
        <Button {...primaryProps} />
      </span>
      <span className="eds-split-button--half eds-split-button--half--end">
        <Menu
          as="button"
          groupedMenuItems={menu.groupedActions?.map((group) => ({
            title: group.title,
            items: group.actions,
          }))}
          menuItems={menu.actions}
          trigger={({ getReferenceProps, ref }) => (
            <IconButton
              {...getReferenceProps()}
              ref={ref}
              disabled={isInactive}
              icon={{ key: 'caret-down' }}
              label={menu.label}
              size={size}
              tooltip={isInactive ? undefined : { text: menu.label }}
              variant={variant}
            />
          )}
        />
      </span>
    </Layout>
  );
});

SplitButton.displayName = 'SplitButton';
